/**
 * Operator conversation takeover on the chat's side of the wire
 * (agion/Agion#3435): which refusal governance returned for a Take over,
 * the words for it and for a refused reply, and the address of the run's
 * conversation in operator mode. Governance decides who may hold a
 * conversation and the runner decides which reply it takes; nothing here
 * checks either.
 */

import { isChatApiError } from './api-failure'

/** The query parameter that opens a run's conversation in operator mode; its value is the hold id. */
export const OPERATOR_MODE_PARAM = 'operator'

/**
 * Why governance refused a Take over, from its code and status: 409
 * ESCALATION_EXPIRED, 409 ESCALATION_HANDOFF_SELF (the run's starter), any
 * other 409 (no longer pending), 403 for a member without the decision
 * right; anything else failed.
 */
export type TakeOverRefusal = 'expired' | 'self' | 'not_pending' | 'forbidden' | 'failed'

export function takeOverRefusalOf(error: unknown): TakeOverRefusal {
  if (!isChatApiError(error)) return 'failed'
  switch (error.code) {
    case 'ESCALATION_EXPIRED':
      return 'expired'
    case 'ESCALATION_HANDOFF_SELF':
      return 'self'
  }
  if (error.status === 409) return 'not_pending'
  if (error.status === 403) return 'forbidden'
  return 'failed'
}

type Translate = (key: string, fallback: string) => string

/** One translated text: its key in the chat bundle and its English fallback. */
interface TextEntry {
  key: string
  fallback: string
}

export const TAKE_OVER_REFUSAL_TEXTS: Record<TakeOverRefusal, TextEntry> = {
  expired: { key: 'chat:operatorTakeover.refusal.expired', fallback: 'The request expired before you took it over.' },
  self: { key: 'chat:operatorTakeover.refusal.self', fallback: 'You started this conversation, so someone else has to take it over.' },
  not_pending: { key: 'chat:operatorTakeover.refusal.notPending', fallback: 'Someone else took this conversation over, or it ended.' },
  forbidden: { key: 'chat:operatorTakeover.refusal.forbidden', fallback: 'You cannot take over this conversation.' },
  failed: { key: 'chat:operatorTakeover.refusal.failed', fallback: 'The conversation was not taken over. Try again.' },
}

export function takeOverRefusalText(t: Translate, refusal: TakeOverRefusal): string {
  const entry = TAKE_OVER_REFUSAL_TEXTS[refusal]
  return t(entry.key, entry.fallback)
}

/**
 * The reasons a reply can be refused (Go missionstream OperatorRefusal*),
 * each with its words. A reason this build does not know reads as the
 * general refusal.
 */
export const OPERATOR_REPLY_REFUSAL_TEXTS: Record<string, TextEntry> = {
  malformed: { key: 'chat:operatorTakeover.replyRefusal.malformed', fallback: 'The reply could not be read. Check the text and send it again.' },
  sender_not_allowed: { key: 'chat:operatorTakeover.replyRefusal.senderNotAllowed', fallback: 'Sign in again to reply.' },
  not_subscribed: { key: 'chat:operatorTakeover.replyRefusal.notSubscribed', fallback: 'This conversation is not open here. Reload it and reply again.' },
  disabled: { key: 'chat:operatorTakeover.replyRefusal.disabled', fallback: 'Taking over conversations is switched off.' },
  no_hold: { key: 'chat:operatorTakeover.replyRefusal.noHold', fallback: 'You no longer hold this conversation.' },
  not_operator: { key: 'chat:operatorTakeover.replyRefusal.notOperator', fallback: 'Another person holds this conversation.' },
  unavailable: { key: 'chat:operatorTakeover.replyRefusal.unavailable', fallback: 'The conversation could not be reached. Try again.' },
}

const REPLY_REFUSED: TextEntry = { key: 'chat:operatorTakeover.replyRefusal.other', fallback: 'The reply was refused.' }

export function operatorReplyRefusalText(t: Translate, reason: string | undefined): string {
  const entry = (reason && Object.prototype.hasOwnProperty.call(OPERATOR_REPLY_REFUSAL_TEXTS, reason)
    ? OPERATOR_REPLY_REFUSAL_TEXTS[reason]
    : undefined) ?? REPLY_REFUSED
  return t(entry.key, entry.fallback)
}

/** The run's conversation in operator mode for one hold. */
export function operatorConversationHref(missionId: string, executionId: string, holdId: string): string {
  const query = new URLSearchParams({ execution: executionId, [OPERATOR_MODE_PARAM]: holdId })
  return `/mission/${encodeURIComponent(missionId)}?${query.toString()}`
}

/** The same run's conversation without operator mode. */
export function runConversationHref(missionId: string, executionId: string): string {
  const query = new URLSearchParams({ execution: executionId })
  return `/mission/${encodeURIComponent(missionId)}?${query.toString()}`
}
